import { Lock, FolderOpen, UserSearch } from "lucide-react";
import { SpecimenIcon } from "../../lib/icons";

const CATEGORIES = ["Identity", "Family", "Place", "Childhood", "Science", "Habits"];

const MemoryCard = ({ specimen, decoded, fresh, onOpen }) => {
  const num = String(specimen.slot).padStart(2, "0");
  if (!decoded) {
    return (
      <div data-testid={`codex-slot-${specimen.slot}`} data-state="locked" className="codex-slot codex-locked">
        <span className="lab-label text-slate-600">{num}</span>
        <Lock size={20} className="text-slate-600" />
        <span className="font-mono text-[10px] tracking-[0.2em] text-slate-600">MEMORY NOT YET RECOVERED</span>
      </div>
    );
  }
  const m = specimen.memory || {};
  return (
    <button data-testid={`codex-slot-${specimen.slot}`} data-state="decoded" onClick={() => onOpen(specimen)} className={`codex-slot codex-decoded text-left items-start ${fresh ? "codex-fresh" : ""}`}>
      <div className="flex w-full items-center justify-between">
        <span className="lab-label">{num} · age {m.age}</span>
        <SpecimenIcon name={specimen.icon} size={18} className="text-emerald-300" />
      </div>
      <span className="font-display text-lg font-bold uppercase tracking-[0.08em] text-slate-50 leading-tight">{m.title}</span>
      <span className="line-clamp-2 text-xs text-slate-400">{m.clue}</span>
    </button>
  );
};

export const CaseFile = ({ specimens, decoded, freshId, subject, onOpen }) => {
  const set = new Set(decoded);
  const recovered = specimens.filter((s) => set.has(s.id));
  const byCat = CATEGORIES.map((c) => [c, recovered.filter((s) => s.memory?.category === c).length]).filter(([, n]) => n > 0);
  return (
    <section data-testid="vocabulary-codex" className="flex flex-col gap-5">
      <div className="flex items-end justify-between gap-4 border-b border-emerald-400/20 pb-3">
        <div>
          <p className="lab-label flex items-center gap-2"><FolderOpen size={12} /> Case file</p>
          <h2 data-testid="case-file-subject" className="font-display text-xl md:text-2xl font-bold uppercase tracking-[0.12em] text-slate-200">
            {subject ? `Subject identified: ${subject.name}` : "Unknown subject"}
          </h2>
        </div>
        <span className="lab-label">{recovered.length}/{specimens.length} memories · tap one to replay</span>
      </div>

      <div data-testid="case-profile" className="grid gap-3 border border-emerald-400/20 bg-slate-950/60 p-4 sm:grid-cols-[auto_1fr]">
        <div className="flex h-20 w-20 items-center justify-center border border-emerald-400/30 bg-emerald-400/5 text-emerald-300">
          <UserSearch size={36} strokeWidth={1.3} />
        </div>
        <div>
          <p className="lab-label">Profile</p>
          {recovered.length === 0 ? (
            <p className="mt-1 text-sm text-slate-500">No memories recovered yet. Place an object on the pad.</p>
          ) : (
            <>
              <div className="mt-1 flex flex-wrap gap-2">
                {byCat.map(([c, n]) => <span key={c} className="border border-amber-300/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-amber-300">{c} · {n}</span>)}
              </div>
              <ul data-testid="case-clues" className="mt-3 grid gap-1 text-sm text-slate-300">
                {recovered.slice(-4).reverse().map((s) => <li key={s.id} className="truncate">· {s.memory?.clue}</li>)}
              </ul>
            </>
          )}
          {subject && (
            <p data-testid="case-identified" className="mt-3 font-mono text-xs uppercase tracking-widest text-rose-300">
              {subject.name} · son of {subject.father} · {subject.home}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
        {specimens.map((s) => <MemoryCard key={s.id} specimen={s} decoded={set.has(s.id)} fresh={s.id === freshId} onOpen={onOpen} />)}
      </div>
    </section>
  );
};
